import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import RatingBadge from "@/components/base/RatingBadge";
import Toast from "@/components/base/Toast";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { useToast } from "@/hooks/useToast";
import { formatDate, getMovieById, getRelated, typeLabel } from "@/lib/movies";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { message, visible, show } = useToast();
  const [shareOpen, setShareOpen] = useState(false);

  useDocumentMeta({
    title: movie ? `${movie.title} - 天天影院影视详情` : "天天影院影视详情",
    description: movie
      ? `${movie.title}：${movie.summary.slice(0, 70)}…天天影院为你提供${movie.title}的剧情简介、演职人员与同类推荐。`
      : "天天影院影视详情页。",
    keywords: movie
      ? `天天影院,${movie.title},${movie.genres.join(",")},${movie.title}剧情`
      : "天天影院,影视详情",
  });

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
        <p className="mt-4 text-foreground-700">抱歉，没有找到该影视作品。</p>
        <Link
          to="/"
          className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm px-5 py-2.5 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const favorite = isFavorite(movie.id);
  const related = getRelated(movie, 4);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const label = typeLabel(movie.type);

  const handleFavorite = () => {
    const added = toggleFavorite(movie.id);
    show(added ? "已添加到收藏" : "已取消收藏");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      show("链接已复制，快去分享吧");
    } catch {
      show("复制失败，请手动复制链接");
    }
    setShareOpen(false);
  };

  const metaRows = [
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "上映日期", value: `${movie.year} 年` },
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "更新时间", value: formatDate(movie.updatedAt) },
  ];

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8 pb-28 lg:pb-8 animate-fade-in">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label, to: `/category/${movie.type}` },
          { label: movie.title },
        ]}
      />

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
          {movie.title}
        </h1>
        {movie.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-md bg-secondary-100 text-secondary-900 text-xs px-2 py-0.5"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 lg:gap-10">
        <div>
          <div className="relative w-full aspect-[2/3] max-w-[280px] lg:max-w-none rounded-lg overflow-hidden bg-background-200">
            <img
              src={movie.cover}
              alt={`${movie.title} 海报`}
              title={`${movie.title} - 天天影院影视详情`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute top-3 left-3">
              <RatingBadge rating={movie.rating} />
            </div>
          </div>

          <div className="hidden lg:flex flex-col gap-3 mt-5">
            <button
              type="button"
              onClick={handleFavorite}
              className={`inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium py-3 transition-colors cursor-pointer whitespace-nowrap ${
                favorite
                  ? "bg-accent-100 text-accent-800 border border-accent-300"
                  : "bg-primary-500 hover:bg-primary-600 text-background-50"
              }`}
            >
              <i className={favorite ? "ri-heart-fill" : "ri-heart-line"} />
              {favorite ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 hover:border-primary-400 text-foreground-800 text-sm font-medium py-3 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-forward-line" />
              分享
            </button>
            <Link
              to={`/category/${movie.type}`}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 hover:border-primary-400 text-foreground-800 text-sm font-medium py-3 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-list-check-2" />
              更多{label}
            </Link>
          </div>

          <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4">
            <h2 className="text-sm font-semibold text-foreground-900 mb-3">影视信息</h2>
            <dl className="space-y-3">
              {metaRows.map((row) => (
                <div key={row.label} className="flex gap-3 text-sm">
                  <dt className="w-20 shrink-0 text-foreground-400">{row.label}</dt>
                  <dd className="text-foreground-800 leading-6">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="min-w-0">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-book-open-line text-primary-500" />
              剧情简介
            </h2>
            <p className="text-sm md:text-[15px] leading-8 text-foreground-700">
              {movie.summary}
            </p>
            <p className="mt-4 text-sm md:text-[15px] leading-8 text-foreground-700">
              作品以{" "}
              <strong className="font-semibold text-foreground-900">
                {movie.genres.join("、")}
              </strong>{" "}
              为基调，由 {movie.director} 执导，{movie.actors.join("、")} 等联袂出演。
              故事在 {movie.region} 的背景下展开，节奏张弛有度，人物刻画细腻，是一部兼具观赏性与思考空间的{movie.year} 年作品。
            </p>
          </section>

          <section className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                相关推荐
              </h2>
              <Link
                to={`/category/${movie.type}`}
                className="text-sm text-primary-600 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line ml-1" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} variant="compact" />
              ))}
            </div>
          </section>
        </div>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-background-200 bg-background-50 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={handleFavorite}
          className={`flex-1 inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium py-3 transition-colors cursor-pointer whitespace-nowrap ${
            favorite
              ? "bg-accent-100 text-accent-800 border border-accent-300"
              : "bg-primary-500 text-background-50"
          }`}
        >
          <i className={favorite ? "ri-heart-fill" : "ri-heart-line"} />
          {favorite ? "已收藏" : "收藏"}
        </button>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-md border border-background-300 text-foreground-800 text-sm font-medium py-3 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-share-forward-line" />
          分享
        </button>
      </div>

      {shareOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-foreground-950/60 animate-fade-in"
          onClick={() => setShareOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-md rounded-lg bg-background-50 p-6 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-foreground-950">
                分享「{movie.title}」
              </h3>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                aria-label="关闭分享"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <p className="text-sm text-foreground-500 mb-3">
              复制下方链接，分享给好友一起追剧
            </p>
            <div className="rounded-md bg-background-100 border border-background-200 px-3 py-3 text-sm text-foreground-700 break-all">
              {shareUrl}
            </div>
            <button
              type="button"
              onClick={handleCopy}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium py-3 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-copy-line" />
              复制链接
            </button>
          </div>
        </div>
      )}

      <Toast message={message} visible={visible} />
    </div>
  );
}